import { useEffect, useState } from 'preact/hooks'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'

const useProductVariant = ({ variants, options, updateCurrentVariant, currentVariant, updateUrl }) => {
  const [selected, setSelected] = useState({})
  const [selectableValues, setSelectableValues] = useState({})

  const getSelectableValues = (a, b) => {
    const values = { ...a }
    const selectedOptions = Object.keys(b)

    if (!selectedOptions.length) {
      return values
    }

    selectedOptions.forEach((selectedOption) => {
      const selectedValue = b[selectedOption]
      const otherOptions = options.filter((option) => option !== selectedOption)

      if (otherOptions.length) {
        otherOptions.forEach((otherOption) => {
          values[otherOption] = values[otherOption].filter((value) =>
            variants.some(
              (variant) =>
                variant.options.includes(value) && variant.options.includes(selectedValue) && variant.available
            )
          )
        })
      } else {
        values[selectedOption] = values[selectedOption].filter((value) => {
          if (options.length > 1) {
            return variants.some(
              (variant) =>
                variant.options.includes(value) && variant.options.includes(selectedValue) && variant.available
            )
          }

          return variants.some((variant) => variant.options.includes(value) && variant.available)
        })
      }
    })

    return values
  }

  const getSelectedVariant = (selectedValues) => {
    if (!Object.keys(selectedValues).length) return null

    const matched = variants.filter((variant) =>
      Object.keys(selectedValues).every((option) => variant.options.includes(selectedValues[option]))
    )

    return matched[0]
  }

  const getInitialSelected = (selectedVariant) => {
    if (!Object.keys(selectedVariant).length) return {}

    const values = {}

    options.forEach((option, index) => {
      const value = selectedVariant.options[index]

      values[option] = value
    })

    return values
  }

  const setUrlParams = (variant) => {
    const urlParams = new URLSearchParams(window.location.search)

    if (!urlParams.has('variant')) {
      urlParams.append('variant', variant.id)
    } else {
      urlParams.set('variant', variant.id)
    }
    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`)
  }

  const availableValues = (() => {
    const values = {}

    options.forEach((option, index) => {
      values[option] = []

      variants.forEach((variant) => {
        const value = variant.options[index]

        if (!values[option].includes(value)) {
          values[option].push(value)
        }
      })
    })

    return values
  })()

  useEffect(() => {
    const selectableOptions = getSelectableValues(availableValues, selected)

    if (selectableOptions) {
      setSelectableValues(selectableOptions)
    }

    const selectedVariant = getSelectedVariant(selected)

    if (selectedVariant && selectedVariant.id !== currentVariant?.id) {
      updateCurrentVariant(selectedVariant)
      if (updateUrl) setUrlParams(selectedVariant)

      globalEmitter.emit(emitterProps.product.updateVariant, selectedVariant)
    }
  }, [selected])

  useEffect(() => {
    if (!currentVariant || !currentVariant.id) return

    const selectedInit = getInitialSelected(currentVariant)
    const isSameSelected = options.every((option) => selectedInit[option] === selected[option])

    if (!isSameSelected) {
      setSelected(selectedInit)
    }
  }, [currentVariant])

  return {
    selected,
    setSelected,
    availableValues,
    selectableValues
  }
}

export default useProductVariant
